import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight, FileSignature, Truck as TruckIcon, X } from "lucide-react";

import { Authed } from "@/components/Authed";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PdfPreview } from "@/components/docs/PdfPreview";
import { formatINR } from "@/lib/format";
import { useLoadingSlipDetail, useUnlinkBiltyFromSlip } from "@/api-client/queries";
import { getErrorMessage } from "@/lib/api-error";

const UNLINKABLE_STATUSES = new Set(["draft", "generated"]);

export const Route = createFileRoute("/loading-slips/$id")({
  component: TruckChallanDetailPage,
});

export function TruckChallanDetailPage() {
  const { id } = Route.useParams();
  const detailQ = useLoadingSlipDetail(id);
  const unlinkMut = useUnlinkBiltyFromSlip(id);
  const src = `/api/loading-slips/${id}/pdf`;
  const slip = detailQ.data;
  const bilties = slip?.bilties ?? [];

  return (
    <Authed>
      <div className="mx-auto max-w-5xl pb-16">
        <div className="flex items-center gap-2 text-xs text-neutral-500 mb-3">
          <Link to="/loading-slips" className="hover:text-navy-800">
            Truck challans
          </Link>
          <ChevronRight className="size-3" />
          <span className="text-neutral-700 tabular">{slip?.slip_no ?? "…"}</span>
        </div>

        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-xl sm:text-2xl font-semibold text-navy-900 tabular">
                {slip?.slip_no ?? "Truck challan"}
              </h1>
              {slip ? (
                <Badge tone={slip.status === "active" ? "accent" : "success"} dotted>
                  {slip.status}
                </Badge>
              ) : null}
            </div>
            {slip ? (
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-neutral-600">
                <span className="tabular">
                  {new Date(slip.loading_date).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className="inline-flex items-center gap-1.5 tabular">
                  <TruckIcon className="size-3.5 text-neutral-400" />
                  {slip.truck_reg_no ?? "—"}
                </span>
                <span>{slip.driver_name ?? "No driver"}</span>
              </div>
            ) : null}
          </div>
          {slip ? (
            slip.lhc_id ? (
              <Link to="/lorry-hire-challans/$id" params={{ id: slip.lhc_id }}>
                <Button variant="secondary" iconLeft={<FileSignature className="size-4" />}>
                  LHC {slip.lhc_no}
                </Button>
              </Link>
            ) : (
              <Link to="/lorry-hire-challans/new" search={{ challanId: slip.id }}>
                <Button
                  variant="primary"
                  iconLeft={<FileSignature className="size-4" />}
                  title="Create the hire agreement for this challan's truck owner"
                >
                  Create lorry hire challan
                </Button>
              </Link>
            )
          ) : null}
        </div>

        {unlinkMut.isError ? (
          <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-900">
            {getErrorMessage(unlinkMut.error)}
          </div>
        ) : null}

        {slip && bilties.length > 0 ? (
          <div className="mb-5 rounded-lg border border-border overflow-hidden">
            <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-2 bg-neutral-50 border-b border-border text-[11px] font-medium uppercase tracking-wide text-neutral-500">
              <div className="col-span-2">GR Ref.</div>
              <div className="col-span-3">Consignor</div>
              <div className="col-span-3">Route</div>
              <div className="col-span-1 text-right">Pkgs</div>
              <div className="col-span-1 text-right">Wt (MT)</div>
              <div className="col-span-2 text-right">Freight</div>
            </div>
            <ul className="divide-y divide-border">
              {bilties.map((r) => (
                <li
                  key={r.bilty_id}
                  className="px-4 py-2.5 grid grid-cols-2 sm:grid-cols-12 gap-2 sm:gap-3 items-center text-sm"
                >
                  <Link
                    to="/bilty/$id"
                    params={{ id: r.bilty_id }}
                    className="col-span-1 sm:col-span-2 tabular font-medium text-navy-800 hover:text-accent-600"
                  >
                    {r.gr_no}
                  </Link>
                  <span className="col-span-1 sm:col-span-3 truncate text-neutral-700">
                    {r.consignor_name}
                  </span>
                  <span className="col-span-1 sm:col-span-3 truncate text-neutral-600">
                    {r.from_city} → {r.to_city}
                  </span>
                  <span className="hidden sm:block col-span-1 text-right tabular">
                    {r.packages}
                  </span>
                  <span className="hidden sm:block col-span-1 text-right tabular">
                    {Number(r.weight_mt).toFixed(3)}
                  </span>
                  <span className="col-span-1 sm:col-span-2 inline-flex items-center justify-end gap-2 text-right tabular font-medium text-navy-900">
                    {formatINR(Number(r.freight))}
                    {/* V38 L3 — once the goods move, taking the challan away
                        would strand the bilty; the API refuses it too. */}
                    {slip.status === "active" && UNLINKABLE_STATUSES.has(r.status) ? (
                      <button
                        type="button"
                        title="Remove from challan"
                        aria-label={`Remove ${r.gr_no} from challan`}
                        disabled={unlinkMut.isPending}
                        onClick={() => unlinkMut.mutate({ biltyId: r.bilty_id })}
                        className="inline-flex items-center justify-center size-6 rounded text-neutral-400 hover:text-danger-700 hover:bg-danger-100/70"
                      >
                        <X className="size-3.5" />
                      </button>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between px-4 py-2.5 bg-neutral-50 border-t border-border text-sm">
              <span className="text-neutral-600">
                {bilties.length} bilty{bilties.length === 1 ? "" : "s"}
              </span>
              <span className="font-semibold tabular text-navy-900">
                {formatINR(Number(slip.total_freight))}
              </span>
            </div>
          </div>
        ) : null}

        <PdfPreview
          src={src}
          filename={`truck-challan-${slip?.slip_no ?? id}.pdf`}
          title="Truck challan PDF"
        />
      </div>
    </Authed>
  );
}
